import { useEffect, useRef, useState } from "react";

/**
 * ===========================================================================
 *  UN BOUTON QU'ON PEUT DEPLACER DU DOIGT
 * ===========================================================================
 *
 *  Le bouton « Tester le son » flotte au-dessus de la page. Utile — personne
 *  ne doit decouvrir a midi que le son etait coupe depuis le matin — mais
 *  fixe, il recouvrait la barre de navigation du bas sur telephone.
 *
 *  On ne peut pas le placer « bien » une fois pour toutes : selon le
 *  telephone, la page ouverte et la hauteur de l'ecran, ce qu'il cache
 *  change. La seule reponse qui marche partout est de laisser la personne
 *  le mettre ou elle veut.
 *
 *  ---------------------------------------------------------------------
 *  TROIS CHOSES QUE CE COMPOSANT DOIT FAIRE, ET QU'ON OUBLIE SOUVENT
 *  ---------------------------------------------------------------------
 *
 *  1. DISTINGUER UN CLIC D'UN GLISSEMENT. Sans cela, deplacer le bouton
 *     declencherait l'action a chaque fois. En-dessous de quelques pixels
 *     de deplacement, c'est un clic ; au-dela, c'est un glissement, et
 *     l'action ne part pas.
 *
 *  2. RESTER DANS L'ECRAN. Un bouton lache hors du cadre, ou glisse sous la
 *     barre du haut, devient irrecuperable. La position est donc bornee —
 *     et rebornee quand l'ecran tourne ou que le clavier s'ouvre.
 *
 *  3. SE SOUVENIR. La place choisie est gardee sur cet appareil. La
 *     redeplacer a chaque ouverture serait pire que de ne pas pouvoir la
 *     changer.
 * ===========================================================================
 */

type Position = { x: number; y: number };

/** Ce qu'il faut de mouvement pour que ce soit un glissement, pas un clic. */
const SEUIL_PX = 8;
/** On garde toujours ce morceau de bouton visible. */
const MARGE = 8;

export default function BoutonFlottant({
  cle,
  onClick,
  title,
  className,
  children,
}: {
  /** Identifie CE bouton : deux boutons flottants ne partagent pas leur place. */
  cle: string;
  onClick: () => void;
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<Position | null>(null);

  // Le geste en cours. Dans une ref, pas dans un etat : ces valeurs changent
  // a chaque pixel et ne doivent rien redessiner.
  const geste = useRef<{
    actif: boolean;
    depuisX: number;
    depuisY: number;
    departX: number;
    departY: number;
    bouge: boolean;
  }>({ actif: false, depuisX: 0, depuisY: 0, departX: 0, departY: 0, bouge: false });

  const borner = (p: Position): Position => {
    const el = ref.current;
    const l = el?.offsetWidth || 140;
    const h = el?.offsetHeight || 40;
    return {
      x: Math.min(Math.max(p.x, MARGE), Math.max(MARGE, window.innerWidth - l - MARGE)),
      y: Math.min(Math.max(p.y, MARGE), Math.max(MARGE, window.innerHeight - h - MARGE)),
    };
  };

  // La place choisie la derniere fois, sur cet appareil.
  useEffect(() => {
    try {
      const brut = localStorage.getItem(`bouton-flottant-${cle}`);
      if (brut) {
        const p = JSON.parse(brut);
        if (Number.isFinite(p?.x) && Number.isFinite(p?.y)) {
          setPos(borner({ x: Number(p.x), y: Number(p.y) }));
          return;
        }
      }
    } catch {
      /* sans memoire, on garde la place par defaut du style */
    }
  }, [cle]);

  // L'ecran tourne, le clavier s'ouvre : on remet le bouton dans le cadre.
  useEffect(() => {
    const auRedimensionnement = () => setPos((p) => (p ? borner(p) : p));
    window.addEventListener("resize", auRedimensionnement);
    window.addEventListener("orientationchange", auRedimensionnement);
    return () => {
      window.removeEventListener("resize", auRedimensionnement);
      window.removeEventListener("orientationchange", auRedimensionnement);
    };
  }, []);

  const auDebut = (e: React.PointerEvent<HTMLButtonElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    geste.current = {
      actif: true,
      depuisX: e.clientX - r.left,
      depuisY: e.clientY - r.top,
      departX: e.clientX,
      departY: e.clientY,
      bouge: false,
    };
    // Le pointeur reste a nous meme si le doigt sort du bouton.
    el.setPointerCapture?.(e.pointerId);
  };

  const auMouvement = (e: React.PointerEvent<HTMLButtonElement>) => {
    const g = geste.current;
    if (!g.actif) return;
    const dx = Math.abs(e.clientX - g.departX);
    const dy = Math.abs(e.clientY - g.departY);
    if (!g.bouge && dx < SEUIL_PX && dy < SEUIL_PX) return;
    g.bouge = true;
    setPos(borner({ x: e.clientX - g.depuisX, y: e.clientY - g.depuisY }));
  };

  const aLaFin = (e: React.PointerEvent<HTMLButtonElement>) => {
    const g = geste.current;
    if (!g.actif) return;
    g.actif = false;
    ref.current?.releasePointerCapture?.(e.pointerId);

    if (g.bouge) {
      // C'etait un deplacement : on garde la place, et SURTOUT on ne
      // declenche pas l'action.
      setPos((p) => {
        if (p) {
          try {
            localStorage.setItem(`bouton-flottant-${cle}`, JSON.stringify(p));
          } catch {
            /* la place ne survivra pas au rechargement, le bouton si */
          }
        }
        return p;
      });
      return;
    }
    onClick();
  };

  return (
    <button
      ref={ref}
      type="button"
      title={title}
      onPointerDown={auDebut}
      onPointerMove={auMouvement}
      onPointerUp={aLaFin}
      onPointerCancel={aLaFin}
      // `touch-none` : sans cela, glisser le bouton ferait defiler la page
      // sous le doigt au lieu de le deplacer.
      className={`touch-none select-none cursor-grab active:cursor-grabbing ${className || ""}`}
      style={
        pos
          ? { position: "fixed", left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
          : undefined
      }
    >
      {children}
    </button>
  );
}
